import type { DisplayConfig } from '@/types/pricebook';

export type LineDetailMode = 'AUTO' | 'DESCRIPTION' | 'DESCRIPTION_AND_AUTO';

/** What a customer document prints under a line's name. */
export interface LineDetailVisibility {
  /** Print the line's own description. */
  description: boolean;
  /** Print the "what's included" list of folded-in parts. */
  included: boolean;
}

/**
 * Honour the catalog item's "What the customer sees on the line" choice.
 *
 * The pricebook form has always previewed this under the heading "How this
 * looks on the estimate", but no document ever read it: every renderer printed
 * the description AND the included-parts list, so the control previewed a
 * result that never happened.
 *
 * An UNSTATED mode is not AUTO. Lines snapshotted before the catalog carried
 * this — every line on every existing estimate — have no answer, and defaulting
 * them to AUTO would silently strip the description from documents that print
 * it today. Unstated therefore keeps printing both, which is exactly what those
 * documents already do; only a line whose item states a mode changes.
 *
 * This decides the line's OWN detail. Whether descriptions appear at all is a
 * separate, document-level toggle the operator sets per document, and it still
 * wins: this can only ever narrow what that toggle allows.
 */
export function lineDetailVisibility(displayConfig?: DisplayConfig | null): LineDetailVisibility {
  switch (displayConfig?.lineDetail) {
    case 'DESCRIPTION':
      return { description: true, included: false };
    case 'AUTO':
      return { description: false, included: true };
    case 'DESCRIPTION_AND_AUTO':
      return { description: true, included: true };
    default:
      return { description: true, included: true };
  }
}
